import React, { useMemo, useCallback } from 'react';
import { Dashboard, SimpleDashboardConfig } from '../Dashboard/Dashboard';
import { DashboardProvider } from './DashboardContext';
import { useDashboard } from '../../../api/dashboard';
import { ErrorMessage } from '../../../components/common/ErrorMessage';
import { decodeDashboardConfig } from '../../../utils/dashboardUtils';
import { safeJsonParse } from '../../../utils/jsonRepairUtils';
import { PleriClient } from '../../../components/Pleri/client';
import { ExpandableContainer } from '../../../components/Pleri/components/MessageContent/markdown/ExpandableContainer';
import { ScaledToFit } from '../../../components/Pleri/components/MessageContent/markdown/ScaledToFit';
import { ScreenshotButton } from '../../../components/Pleri/components/MessageContent/markdown/ScreenshotButton';
import { PDFButton } from '../../../components/Pleri/components/MessageContent/markdown/PDFButton';
import { ErrorBoundary } from '../../../components/Pleri/components/MessageContent/markdown/ErrorBoundary';
import { DashboardSkeleton } from './SkeletonLoaders';
import { MessageCircle } from 'lucide-react';
import { useDashboardChat } from './useDashboardChat';

import { useTranslation } from 'react-i18next';

export interface UniversalDashboardRendererProps {
  // Data source (provide ONE of these)
  dashboardId?: string;
  code?: string;
  schema?: string;
  config?: SimpleDashboardConfig;

  // Display options
  variant?: 'full' | 'mini' | 'scaled' | 'expandable';
  className?: string;
  height?: string;

  // Features
  enableExpand?: boolean;
  enablePDF?: boolean;
  enableScreenshot?: boolean;
  showAskPleri?: boolean;
  showActionToolbar?: boolean;

  title?: string;
  dashboardTitle?: string;
  dashboardScope?: string;

  // Scaling options
  enableScaling?: boolean;
  designWidth?: number;
  maxScale?: number;
  minScale?: number;

  // Callbacks
  onConfigLoaded?: (config: SimpleDashboardConfig) => void;
  onError?: (error: Error) => void;
  onExpand?: () => void;

  // Error boundary
  enableErrorBoundary?: boolean;
  errorBoundaryFallback?: React.ReactNode;
  onErrorBoundary?: (error: Error, errorInfo: React.ErrorInfo) => void;

  // Streaming flag for LLM tool use chunks
  isStreaming?: boolean;
}

export const UniversalDashboardRenderer: React.FC<
  UniversalDashboardRendererProps
> = ({
  dashboardId,
  code,
  schema,
  config: propConfig,
  variant = 'full',
  className = '',
  height,
  enableExpand = false,
  enablePDF = false,
  enableScreenshot = false,
  showAskPleri = true,
  showActionToolbar = true,
  enableScaling = false,
  designWidth = 1400,
  maxScale = 0.5,
  minScale = 0.25,
  title = 'Dashboard',
  dashboardTitle,
  dashboardScope,
  onConfigLoaded,
  onError,
  onExpand,
  enableErrorBoundary = true,
  errorBoundaryFallback,
  onErrorBoundary,
  isStreaming = false,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  // Fetch dashboard by ID if provided
  const dashboardQuery = useDashboard(dashboardId || '', 'user', 'user');
  const dashboardRef = React.useRef<HTMLDivElement>(null);

  // Dashboard chat functionality for Ask Pleri button
  const { openDashboardWithChat } = useDashboardChat({
    componentName: 'Dashboard',
    contextType: 'widget',
  });

  // Resolve dashboard config from various sources
  const dashboardConfig = useMemo(() => {
    try {
      if (propConfig) {
        onConfigLoaded?.(propConfig);
        return propConfig;
      }

      if (dashboardId && dashboardQuery.data) {
        const config = dashboardQuery.data as unknown as SimpleDashboardConfig;
        onConfigLoaded?.(config);
        return config;
      }

      if (schema) {
        const config = decodeDashboardConfig(schema);
        onConfigLoaded?.(config);
        return config;
      }

      if (code) {
        const parsed = safeJsonParse<SimpleDashboardConfig>(code);
        if (Object.keys(parsed).length === 0) {
          return null;
        }
        if (parsed) {
          onConfigLoaded?.(parsed);
          return parsed;
        }
      }

      return null;
    } catch (error) {
      const err =
        error instanceof Error
          ? error
          : new Error('Failed to parse dashboard config');
      onError?.(err);
      return null;
    }
  }, [
    propConfig,
    dashboardId,
    dashboardQuery.data,
    schema,
    code,
    onConfigLoaded,
    onError,
  ]);

  // Create a stable key for error boundary that changes when config changes
  // This will reset the error boundary when the dashboard config is updated
  // const errorBoundaryKey = dashboardConfig
  //   ? JSON.stringify(dashboardConfig)
  //   : 'no-config';

  // Create custom error boundary fallback for dashboard errors
  const createDashboardErrorFallback = (error?: Error) => {
    return (
      <div
        className={`flex items-center justify-center p-8 ${
          height ? `h-[${height}]` : 'h-64'
        } ${className}`}
      >
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {t('dashboardError')}
          </h3>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t('somethingWentWrongWhileRenderingTheDashboard')}
          </p>
          {error && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-red-600 hover:text-red-800">
                {t('showErrorDetails')}
              </summary>
              <pre className="mt-2 overflow-auto rounded-md bg-red-50 p-3 text-xs text-red-700 dark:bg-red-950">
                {error.message}
              </pre>
            </details>
          )}
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            {t('refreshPage')}
          </button>
        </div>
      </div>
    );
  };

  // Handle focus view expansion
  const handleExpand = useCallback(() => {
    if (!dashboardConfig) {
      return;
    }

    const finalTitle = dashboardTitle || dashboardConfig.title || title;
    const focusedContent = (
      <UniversalDashboardRenderer
        config={dashboardConfig}
        variant="full"
        enablePDF
        enableScreenshot
        enableScaling
        showAskPleri={false}
        designWidth={designWidth}
        maxScale={maxScale}
        minScale={minScale}
        dashboardId={dashboardId}
        dashboardTitle={finalTitle}
        dashboardScope={dashboardScope}
        isStreaming={isStreaming}
      />
    );

    PleriClient.updateChatInput({
      prompt: ``,
      useExistingConversation: true,
      appendContext: true,
      focusedView: {
        content: focusedContent,
        title: t('focusedView', { finalTitle }),
        printMode: 'image',
      },
    });

    onExpand?.();
  }, [
    dashboardConfig,
    dashboardId,
    dashboardTitle,
    dashboardScope,
    title,
    designWidth,
    maxScale,
    minScale,
    isStreaming,
    onExpand,
    t,
  ]);

  // Render pure dashboard content (for printing/canvas capture)
  const renderPureDashboard = () => {
    // Handle loading states
    if (dashboardId && dashboardQuery.isLoading) {
      return <DashboardSkeleton />;
    }

    // Handle error states - show loading if streaming, otherwise show error
    if (dashboardId && dashboardQuery.error) {
      if (isStreaming) {
        return <DashboardSkeleton />;
      }

      const errorMessage =
        (dashboardQuery.error as Error)?.message || t('failedToLoadDashboard');
      return <ErrorMessage message={errorMessage} onDismiss={() => {}} />;
    }

    // Handle no config state - show loading if streaming, otherwise show no data message
    if (!dashboardConfig || Object.keys(dashboardConfig).length === 0) {
      if (isStreaming) {
        return <DashboardSkeleton />;
      }

      return (
        <div className="flex items-center justify-center p-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {t('noDashboardData')}
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {t('pleaseProvideADashboardIdConfigSchema')}
            </p>
          </div>
        </div>
      );
    }

    const finalTitle = dashboardTitle || dashboardConfig.title || title;

    // Use original dashboard config
    const configWithDisabledChat = dashboardConfig;

    const dashboardContent = (
      <DashboardProvider
        dashboardId={dashboardId || 'universal-dashboard'}
        dashboardTitle={finalTitle}
        dashboardScope={dashboardScope || 'universal'}
      >
        <Dashboard config={configWithDisabledChat} isStreaming={isStreaming} />
      </DashboardProvider>
    );

    // Wrap Dashboard component with error boundary if enabled
    if (!enableErrorBoundary) {
      return dashboardContent;
    }

    return (
      <ErrorBoundary
        // key={errorBoundaryKey}
        fallback={errorBoundaryFallback || createDashboardErrorFallback()}
        onError={onErrorBoundary}
      >
        {dashboardContent}
      </ErrorBoundary>
    );
  };

  // Render dashboard with wrapper (includes ref for capture)
  const renderDashboard = () => {
    return (
      <div ref={dashboardRef} className="relative">
        {renderPureDashboard()}
      </div>
    );
  };

  // Create expand button if enabled
  const renderExpandButton = () => {
    if (!enableExpand || !dashboardConfig) {
      return null;
    }

    return (
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={handleExpand}
          className="flex items-center gap-1.5 rounded-md bg-orange-50 px-3 py-1.5 text-sm font-medium text-orange-600 transition-colors hover:bg-orange-100 dark:bg-gray-800"
          title={t('openDashboardInFocusedView')}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
            />
          </svg>
          {t('focus')}
        </button>
      </div>
    );
  };

  // Create Ask Pleri button (using same functionality as Dashboard.tsx blue button)
  const createAskPleriButton = () => {
    const handleChatClick = () => {
      openDashboardWithChat(
        undefined, // Use current dashboard ID from context
        `I'd like to understand this dashboard better. Can you help me analyze the data and insights?`,
      );
    };

    return (
      <button
        onClick={handleChatClick}
        className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:outline-none"
      >
        <MessageCircle size={16} />
        {t('askPleri')}
      </button>
    );
  };

  // Create print buttons for full variant (using same style as ExpandableContainer)
  const createPrintButtons = (title: string) => {
    const buttons = [];

    if (enableScreenshot) {
      buttons.push(
        <ScreenshotButton
          key="screenshot"
          targetRef={dashboardRef}
          filename={`${title.toLowerCase().replace(/\s+/g, '-')}-screenshot.png`}
          title={t('takeScreenshotOfDashboard')}
        />,
      );
    }

    if (enablePDF) {
      buttons.push(
        <PDFButton
          key="pdf"
          targetRef={dashboardRef}
          filename={`${title.toLowerCase().replace(/\s+/g, '-')}.pdf`}
          title={t('downloadDashboardAsPdf')}
        />,
      );
    }

    return buttons;
  };

  // Render based on variant
  const containerClass = `${height ? `h-[${height}]` : 'h-full'} ${className}`;
  const finalTitle = dashboardTitle || dashboardConfig?.title || title;

  // Mini variant - for chat (minimized panel with border and scaling)
  if (variant === 'mini') {
    return (
      <div
        className={`relative overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-900 ${containerClass}`}
      >
        <div className="origin-top-left scale-50 transform">
          <div style={{ width: '200%', height: '200%' }}>
            {renderDashboard()}
          </div>
        </div>
        {renderExpandButton()}
      </div>
    );
  }

  // Expandable variant - with ExpandableContainer (print, PDF, screenshot)
  if (variant === 'expandable') {
    return (
      <ExpandableContainer
        title={finalTitle}
        minimizedHeight={height || '350px'}
        enableScreenshot={enableScreenshot}
        enablePDF={enablePDF}
        pdfFilename={`${finalTitle.toLowerCase().replace(/\s+/g, '-')}-report.pdf`}
        className="focus-button-always-visible"
        defaultExpanded={isStreaming}
      >
        {(isExpanded) => (
          <ScaledToFit
            designWidth={designWidth}
            maxScale={maxScale}
            enabled={enableScaling && !isExpanded}
          >
            {renderDashboard()}
          </ScaledToFit>
        )}
      </ExpandableContainer>
    );
  }

  // Scaled variant - with ScaledToFit
  if (variant === 'scaled') {
    return (
      <div className={`relative ${containerClass}`}>
        <ScaledToFit
          designWidth={designWidth}
          maxScale={maxScale}
          enabled={enableScaling}
        >
          {renderDashboard()}
        </ScaledToFit>
      </div>
    );
  }

  // Full variant - with UI buttons OUTSIDE the printable content
  return (
    <div className={`${containerClass}`}>
      {/* Action buttons - compact toolbar on the right */}
      {showActionToolbar && (showAskPleri || enableScreenshot || enablePDF) && (
        <div className="mb-4 flex justify-end">
          <div className="flex items-center gap-2">
            {/* Ask Pleri button */}
            {showAskPleri && createAskPleriButton()}

            {/* Print buttons */}
            {createPrintButtons(finalTitle)}
          </div>
        </div>
      )}

      {/* Pure dashboard content for printing */}
      {renderDashboard()}
    </div>
  );
};
